import type { WidgetSettingsResponse } from '../types/widget-settings.js';

/**
 * Base fallback when backend omits or sends empty settings keys.
 * Neutral slate / zinc palette — unbranded until API theme arrives.
 */
export const DEFAULT_WIDGET_SETTINGS: WidgetSettingsResponse = {
  BS_BG_COLOR_MESSAGES: '#52525b',
  BS_BORDER_RADIUS: '50',
  BS_INDENT_FROM_BELOW: '40',
  BS_POPUP_BG_COLOR: '#FFFFFF',
  BS_POPUP_BUTTON_BG_COLOR: '#52525b',
  BS_POPUP_BUTTON_TEXT_COLOR: '#ffffff',
  BS_POPUP_TEXT_COLOR: '#18181b',
  BS_POSITIONING: 'bottom_right',
  BS_MOBILE_LAUNCHER_MODE: 'fixed',
  BS_RETREAT_FROM_EDGE: '40',
  BS_SIZE: '56',
  BS_TEXT_COLOR: '#ffffff',

  WS_BG_COLOR: '#f4f4f5',
  WS_BG_COLOR_HEADER: '#3f3f46',
  WS_BG_COLOR_MESSAGES: '#ffffff',
  WS_BG_COLOR_USER_MESSAGES: '#e4e4e7',
  WS_BORDER_RADIUS: '16',
  WS_FONT_FAMILY: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  WS_TEXT_COLOR: '#18181b',
  WS_TEXT_COLOR_HEADER: '#fafafa',
  WS_TEXT_COLOR_MESSAGES: '#18181b',
  WS_TEXT_COLOR_USER_MESSAGES: '#18181b',
  WS_WIDGET_SIZE: 'small',

  CONTEXT_ENABLED: 'true',
  WIDGET_VISIBLE: 'true',
  PROACTIVE_GREETING_ENABLED: 'true',
  LANGUAGES: 'uk,en',
  MESSAGE_SCHEDULE: '{}',
  SHOP_NAME: 'Demo Shop',
  URL: '',
  WELCOME_MESSAGE: JSON.stringify({
    uk: 'Вітаємо!',
    en: 'Welcome!',
  }),

  EXIT_INTENT_MESSAGE: JSON.stringify({
    uk: 'Зачекайте! Маєте питання?',
    en: 'Wait! Any questions?',
  }),

  AI_AGENT_NAME: 'Teo',
  AI_AVATAR_URL: '',
  CONTEXT_POPUP_DELAY_SECONDS: '15',
  CONTEXT_POPUP_DELAY_BY_PAGE_TYPE: JSON.stringify({
    home: 10,
    category: 15,
    product: 12,
    cart: 14,
    checkout: 5,
    blog: 20,
    search: 15,
    other: 15,
  }),
  PROACTIVE_GREETING_AUTO_OPEN_CHAT: 'false',
  PROACTIVE_GREETING_QUICK_REPLIES: 'false',
  PROACTIVE_GREETING_QUICK_REPLY_COUNT: '3',
  PROACTIVE_GREETING_QUICK_REPLY_SOURCE: 'ai',
  PROACTIVE_GREETING_CUSTOM_QUICK_REPLY: JSON.stringify({
    uk: 'Підкажіть, будь ласка',
    en: 'Please help me',
  }),
  PROACTIVE_GREETING_MODE: 'first_dialog_reengage',
  PROACTIVE_GREETING_REENGAGE_SECONDS: '1800',
  POWERED_BY_ENABLED: 'false',
  POWERED_BY_TEXT: 'Powered by Teo',
  POWERED_BY_URL: 'https://aiteo.tech',
};

/**
 * Keys that may legitimately be empty from the backend
 * (empty means “use empty”, not “use default”).
 */
const ALLOW_EMPTY_KEYS = new Set<keyof WidgetSettingsResponse>([
  'AI_AVATAR_URL',
  'POWERED_BY_TEXT',
  'URL',
  'CONTEXT_POPUP_DELAY_BY_PAGE_TYPE',
]);

/**
 * Merge partial / incomplete API settings over {@link DEFAULT_WIDGET_SETTINGS}.
 * Missing, null, or blank string values fall back to the neutral defaults
 * (except keys in {@link ALLOW_EMPTY_KEYS}).
 */
export function mergeWidgetSettingsWithDefaults(
  raw: Partial<WidgetSettingsResponse> | null | undefined,
): WidgetSettingsResponse {
  const source = raw ?? {};
  const merged = { ...DEFAULT_WIDGET_SETTINGS };

  for (const key of Object.keys(
    DEFAULT_WIDGET_SETTINGS,
  ) as Array<keyof WidgetSettingsResponse>) {
    const value = source[key];
    if (value == null) continue;
    if (value.trim() === '' && !ALLOW_EMPTY_KEYS.has(key)) continue;
    // Indexed assignment across WidgetSettingsResponse keys needs a cast.
    merged[key] = value as never;
  }

  return merged;
}
